import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import MascotAvatar from "./MascotAvatar";
import {
  ROLE_OPTIONS,
  LEVEL_OPTIONS,
  NEED_OPTIONS,
  CURRICULUM_OPTIONS,
  shouldAskCurriculum,
  shouldAskTextbookPrefs,
} from "../../constants";

const BTN_BASE = "rounded-xl border transition";
const BTN_ACTIVE = "border-[#A855F7] bg-purple-50 text-[#7C3AED] font-semibold";
const BTN_IDLE = "border-black/10 bg-white text-gray-600 hover:border-[#A855F7]/50";

function OptionGrid({ options, value, onSelect, columns, testIdPrefix, sizeClass }) {
  return (
    <div className={`grid gap-2 ${columns}`}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onSelect(o.value)}
          className={`${BTN_BASE} ${sizeClass} ${value === o.value ? BTN_ACTIVE : BTN_IDLE}`}
          data-testid={`${testIdPrefix}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const EMPTY_PROFILE = {
  name: "",
  role: "",
  level: "",
  curriculum: "",
  textbook_prefs: "",
  need: "",
};

/**
 * Multi-step onboarding flow shown inside the floating widget panel.
 * Collects name → role → level → (curriculum, textbook prefs if student) → need,
 * then hands the profile back via `onDone`.
 */
export default function WidgetOnboardingForm({ onDone }) {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(EMPTY_PROFILE);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const steps = useMemo(() => {
    const list = [
      {
        title: "What should I call you?",
        hint: "Just a first name is fine — you can skip it.",
        render: () => (
          <Input
            autoFocus
            value={profile.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Your name (optional)"
            className="rounded-xl"
            data-testid="onboard-name-input"
          />
        ),
        canNext: true,
      },
      {
        title: "You are a...",
        hint: "Helps me tune advice to your context.",
        render: () => (
          <OptionGrid
            options={ROLE_OPTIONS}
            value={profile.role}
            onSelect={(v) => setField("role", v)}
            columns="grid-cols-3"
            sizeClass="px-2 py-2 text-xs font-semibold"
            testIdPrefix="onboard-role"
          />
        ),
        canNext: !!profile.role,
      },
      {
        title: "Which grade / stage?",
        hint: "Pick your current class or level.",
        render: () => (
          <OptionGrid
            options={LEVEL_OPTIONS}
            value={profile.level}
            onSelect={(v) => setField("level", v)}
            columns="grid-cols-3"
            sizeClass="px-1.5 py-1.5 text-xs font-medium"
            testIdPrefix="onboard-level"
          />
        ),
        canNext: !!profile.level,
      },
    ];

    if (shouldAskCurriculum(profile.role)) {
      list.push({
        title: "Curriculum / board?",
        hint: "So I can pull references that match your syllabus.",
        render: () => (
          <OptionGrid
            options={CURRICULUM_OPTIONS}
            value={profile.curriculum}
            onSelect={(v) => setField("curriculum", v)}
            columns="grid-cols-2"
            sizeClass="px-2.5 py-2 text-xs font-medium"
            testIdPrefix="onboard-curriculum"
          />
        ),
        canNext: !!profile.curriculum,
      });
    }

    if (shouldAskTextbookPrefs(profile.role, profile.level)) {
      list.push({
        title: "Textbooks or exam prep?",
        hint: "Optional — e.g. \"NCERT + RD Sharma\" or \"JEE Mains, RS Aggarwal\".",
        render: () => (
          <Input
            value={profile.textbook_prefs}
            onChange={(e) => setField("textbook_prefs", e.target.value)}
            placeholder="e.g. NCERT + RD Sharma, JEE Mains"
            className="rounded-xl"
            data-testid="onboard-textbook-input"
            maxLength={240}
          />
        ),
        canNext: true,
      });
    }

    list.push({
      title: "What do you need help with?",
      hint: "Pick the closest fit — you can change topics any time.",
      render: () => (
        <OptionGrid
          options={NEED_OPTIONS}
          value={profile.need}
          onSelect={(v) => setField("need", v)}
          columns="grid-cols-2"
          sizeClass="px-2.5 py-2.5 text-xs font-medium"
          testIdPrefix="onboard-need"
        />
      ),
      canNext: !!profile.need,
    });

    return list;
  }, [profile]);

  // If the visible step index outgrows the (now shorter) step list because the
  // user changed role/level, snap it back inside bounds.
  const safeStep = Math.min(step, steps.length - 1);
  const current = steps[safeStep];
  const isLast = safeStep === steps.length - 1;

  const handleNext = () => {
    if (isLast) onDone(profile);
    else setStep(safeStep + 1);
  };

  return (
    <div className="flex h-full flex-col bg-[#FCF9FF]">
      <div className="border-b border-purple-100 px-5 py-2.5 shrink-0">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-[#7C3AED]">
            Step {safeStep + 1} of {steps.length}
          </span>
          <button
            onClick={() => onDone(EMPTY_PROFILE)}
            className="text-gray-500 hover:text-[#7C3AED]"
            data-testid="onboard-skip"
          >
            Skip
          </button>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-purple-100">
          <motion.div
            className="h-full bg-gradient-to-r from-[#7C3AED] via-[#A855F7] to-[#EC4899]"
            initial={{ width: 0 }}
            animate={{ width: `${((safeStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-3.5 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-3">
          <div className="shrink-0">
            <MascotAvatar state="greet" size={40} variant="compact" />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#5b1f6e] leading-tight">{current.title}</h3>
            <p className="text-xs text-purple-700/70">{current.hint}</p>
          </div>
        </div>
        <motion.div
          key={safeStep}
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div>{current.render()}</div>
        </motion.div>
      </div>

      <div className="border-t border-purple-100 bg-white px-5 py-2.5 shrink-0">
        <div className="flex items-center justify-between gap-3">
          {safeStep > 0 ? (
            <button
              onClick={() => setStep(safeStep - 1)}
              className="text-xs font-semibold text-gray-500 hover:text-[#7C3AED]"
              data-testid="onboard-back"
            >
              Back
            </button>
          ) : (
            <span />
          )}
          <Button
            onClick={handleNext}
            disabled={!current.canNext}
            className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] px-5 py-2 h-9 text-xs sm:text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 shadow-md shadow-purple-500/25"
            data-testid="onboard-next"
          >
            {isLast ? "Start chat" : "Next"} <ArrowRight className="ml-1.5" size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
